import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, RefreshCcw, X, Trash2, RotateCcw, CheckSquare, Square,
  ExternalLink, SlidersHorizontal, ChevronDown, Check,
  Pencil, Save, History, MessageSquare, GitBranch, Edit3, UserCheck,
  AlertCircle, AlertTriangle, Send,
} from "lucide-react";
import { api } from "../../lib/api";
import CountyCombobox from "../forms/CountyCombobox";
import SchoolSelector from "../SchoolSelector";
import TeacherProfileFields, { coerceLegacyTeacherProfile } from "../TeacherProfileFields";
import { ROMANIAN_COUNTIES } from "../../lib/counties";
import { failureReason, glossFor } from "../../lib/paymentReason";
import MinistryDataPanel from "./MinistryDataPanel";
import ExportButton from "./ExportButton";
import { downloadBlob } from "../../lib/downloadBlob";
import { exportName, exportPath } from "../../lib/exportFile";
import EnrollmentAssessmentPanel from "./assessment/EnrollmentAssessmentPanel";
import LimoEnrollmentPanel from "./LimoEnrollmentPanel";
import { moveQuestion } from "../../lib/limoGroup";
import { canNotifyGroupChange, groupChangeQuestion, scheduleNote, whyNotNotify } from "../../lib/groupChange";
import { deadlineNote } from "../../lib/paymentDeadline";
import ContractPanel from "./ContractPanel";
import ReferralCodePanel from "./ReferralCodePanel";
import AssignSelector from "./AssignSelector";
import Pagination from "./Pagination";
import GroupSessionsList from "../GroupSessionsList";
import { shortStatus } from "../../lib/contractRegister";
import { TONE as CONTRACT_TONE } from "../../lib/contractStatus";
import PortalEmailField from "./PortalEmailField";
import { isFped } from "../../lib/regime";

// M916 — Server-side pagination default for the enrollments table.
const DEFAULT_PAGE_SIZE = 25;

const STATUSES = [
  { v: "submitted", label: "Trimisă", cls: "bg-[#3F8EBA]/15 text-[#1f5d80]" },
  { v: "pending_payment", label: "Așteaptă plată", cls: "bg-[#F59E0B]/20 text-[#92400E]" },
  { v: "confirmed", label: "Confirmată", cls: "bg-[#965EA7]/15 text-[#7A4986]" },
  { v: "paid", label: "Plătită", cls: "bg-[#16a34a]/20 text-[#065F46]" },
  { v: "cancelled", label: "Anulată", cls: "bg-[#6B7280]/15 text-[#373A45]" },
  { v: "failed", label: "Eșuată", cls: "bg-[#E63946]/15 text-[#9b1f2c]" },
  { v: "abandoned", label: "Abandonată", cls: "bg-[#6B7280]/15 text-[#373A45]" },
  { v: "refunded", label: "Returnată", cls: "bg-[#6B7280]/15 text-[#373A45]" },
];
const STATUS_BY = STATUSES.reduce((m, s) => { m[s.v] = s; return m; }, {});

// CRM operational stages (PR2). Derived from `status` when admin hasn't
// explicitly set one, so the dropdown is always meaningful.
export const CRM_STAGES = [
  { v: "new",                          label: "Nou",                    cls: "bg-[#3F8EBA]/15 text-[#1f5d80]" },
  { v: "contacted",                    label: "Contactat",              cls: "bg-[#965EA7]/15 text-[#7A4986]" },
  { v: "awaiting_confirmation",        label: "Așteaptă confirmare",    cls: "bg-[#F59E0B]/20 text-[#92400E]" },
  { v: "waiting_payment_activation",   label: "Așteaptă plata",         cls: "bg-[#F59E0B]/12 text-[#a85a06]" },
  { v: "enrolled",                     label: "Înscris",                cls: "bg-[#16a34a]/20 text-[#065F46]" },
  { v: "rejected",                     label: "Respins",                cls: "bg-[#E63946]/15 text-[#9b1f2c]" },
];
const CRM_BY = CRM_STAGES.reduce((m, s) => { m[s.v] = s; return m; }, {});

const PAYMENT_STATUS_CLS = {
  paid: "bg-[#16a34a]/20 text-[#065F46]",
  pending: "bg-[#F59E0B]/20 text-[#92400E]",
  failed: "bg-[#E63946]/15 text-[#9b1f2c]",
  cancelled: "bg-[#6B7280]/15 text-[#373A45]",
  abandoned: "bg-[#6B7280]/15 text-[#373A45]",
};

const FRIENDLY_SOURCES = [
  "Facebook", "Instagram", "Google Ads", "Organic",
  "LinkedIn", "YouTube", "TikTok", "Twitter",
  "Logiscool", "Trainer", "Email", "Direct", "Unknown",
];
const FRIENDLY_SOURCE_TONE = {
  Facebook: "bg-[#1877F2]/12 text-[#1265CC]",
  Instagram: "bg-[#E4405F]/12 text-[#C72B49]",
  "Google Ads": "bg-[#34A853]/12 text-[#1E7836]",
  Organic: "bg-[#34A853]/12 text-[#1E7836]",
  LinkedIn: "bg-[#0A66C2]/12 text-[#074E94]",
  YouTube: "bg-[#FF0000]/10 text-[#8C0000]",
  TikTok: "bg-[#373A45]/12 text-[#373A45]",
  Logiscool: "bg-[#F5821F]/12 text-[#B45C10]",
  Trainer: "bg-[#965EA7]/[0.12] text-[#7A4986]",
  Twitter: "bg-[#373A45]/12 text-[#373A45]",
  Email: "bg-[#F59E0B]/15 text-[#92400E]",
  Direct: "bg-[#6B7280]/15 text-[#373A45]",
  Unknown: "bg-[#6B7280]/10 text-[#6B7280]",
};

// Where each enrollment stands on the accreditation dossier. Mirrors
// `COLLECTION_STATUSES` on the backend — `refused` is a state, not a gap, so
// somebody who declined drops off the call list instead of being rung weekly.
const MINISTRY_BY = {
  missing: { label: "Lipsă", cls: "bg-[#6B7280]/10 text-[#6B7280]" },
  email_sent: { label: "Email trimis", cls: "bg-[#3F8EBA]/15 text-[#2E6F94]" },
  by_participant: { label: "Completat", cls: "bg-[#519872]/15 text-[#2F6B4E]" },
  by_operator: { label: "Telefonic", cls: "bg-[#519872]/15 text-[#2F6B4E]" },
  refused: { label: "Refuză", cls: "bg-[#E63946]/12 text-[#9b1f2c]" },
};

export default function EnrollmentsTab({ canMutate, isOwner, currentRole, initialDrawerId, onInitialDrawerConsumed }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [expiredTotal, setExpiredTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // M916 — Pagination state (URL-synced via ?page=N).
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const setPage = useCallback((next) => {
    const nextSp = new URLSearchParams(searchParams);
    if (next <= 1) nextSp.delete("page");
    else nextSp.set("page", String(next));
    setSearchParams(nextSp, { replace: false });
  }, [searchParams, setSearchParams]);
  const [pageMeta, setPageMeta] = useState({
    page: 1, page_size: DEFAULT_PAGE_SIZE, total_pages: 1, has_next: false, has_prev: false,
  });
  const [filters, setFilters] = useState({
    q: "", status: "", crm_stage: "", enrollment_mode: "", email: "",
    friendly_source: "", consent_marketing: "",
    county: "", specializare: "", ministry_status: "", regime: "", referral: "",
    date_from: "", date_to: "",
    include_deleted: false, test_only: false, mine_only: false,
    // Closed by the 48-hour payment deadline: out of the way unless asked for.
    expired_only: false,
  });
  const [qDraft, setQDraft] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, q: qDraft })), 300);
    return () => clearTimeout(t);
  }, [qDraft]);
  const [drawerId, setDrawerId] = useState(null);
  // Contracts are owner/admin only (their documents carry a CNP).
  const seesContracts = ["owner", "admin", "admin_fped"].includes(currentRole);
  const [contractsByEnrollment, setContractsByEnrollment] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Open the drawer for the newly-converted enrollment when the parent passes one.
  useEffect(() => {
    if (initialDrawerId) {
      setDrawerId(initialDrawerId);
      onInitialDrawerConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDrawerId]);

  // `?e=<id>` opens that enrolment (15.10.2026): the link in the team's email
  // about a reopening request, and the rows of a group's initial assessment.
  // Taken off the address once read, so closing the drawer stays closed.
  const linkedId = searchParams.get("e");
  useEffect(() => {
    if (!linkedId) return;
    setDrawerId(linkedId);
    const next = new URLSearchParams(searchParams);
    next.delete("e");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedId]);

  const buildQuery = useCallback(() => {
    const qs = new URLSearchParams();
    if (filters.q) qs.set("q", filters.q);
    if (filters.status) qs.set("status", filters.status);
    if (filters.crm_stage) qs.set("crm_stage", filters.crm_stage);
    if (filters.enrollment_mode) qs.set("enrollment_mode", filters.enrollment_mode);
    if (filters.email) qs.set("email", filters.email);
    if (filters.friendly_source) qs.set("friendly_source", filters.friendly_source);
    if (filters.consent_marketing === "yes") qs.set("consent_marketing", "true");
    if (filters.consent_marketing === "no") qs.set("consent_marketing", "false");
    if (filters.county) qs.set("county", filters.county);
    if (filters.ministry_status) qs.set("ministry_status", filters.ministry_status);
    if (filters.regime) qs.set("regime", filters.regime);
    if (filters.referral) qs.set("referral", filters.referral);
    if (filters.specializare) qs.set("specializare", filters.specializare);
    if (filters.date_from) qs.set("date_from", filters.date_from);
    if (filters.date_to) qs.set("date_to", filters.date_to);
    if (filters.include_deleted) qs.set("include_deleted", "true");
    if (filters.expired_only) qs.set("expired_only", "true");
    if (filters.test_only) qs.set("test_only", "true");
    if (filters.mine_only) qs.set("mine_only", "true");
    return qs;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      qs.set("page", String(page));
      qs.set("page_size", String(DEFAULT_PAGE_SIZE));
      const r = await api.get(`/admin/enrollments?${qs.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setExpiredTotal(r.data.expired_total || 0);
      setPageMeta({
        page: r.data.page ?? 1,
        page_size: r.data.page_size ?? DEFAULT_PAGE_SIZE,
        total_pages: r.data.total_pages ?? 1,
        has_next: !!r.data.has_next,
        has_prev: !!r.data.has_prev,
      });
      setSelected(new Set());
    } catch {
      toast.error("Nu am putut încărca înscrierile.");
    } finally {
      setLoading(false);
    }
  }, [buildQuery, page]);
  useEffect(() => { load(); }, [load]);

  // M916 — Reset to page 1 whenever the filter set changes. Skip the
  // initial mount so a deep-link like ?page=3 survives refresh.
  const lastFilterSigRef = useRef(null);
  const filterSig = useMemo(() => buildQuery().toString(), [buildQuery]);
  useEffect(() => {
    if (lastFilterSigRef.current === null) {
      lastFilterSigRef.current = filterSig;
      return;
    }
    if (lastFilterSigRef.current !== filterSig) {
      lastFilterSigRef.current = filterSig;
      setPage(1);
    }
  }, [filterSig, setPage]);

  // Deliberately not the filtered set: this file is the dossier, and a
  // partial one submitted by accident is worse than one assembled on
  // purpose. Owner/admin only, and the download is audited server-side.
  const exportMinistryCsv = (format) => downloadBlob(exportPath("/admin/ministry-data/export", null, format),
    exportName("date-minister", format), null, "Export pornit. Fișierul conține CNP-uri — tratează-l ca atare.");

  const exportCsv = (format) => downloadBlob(exportPath("/admin/enrollments/export", buildQuery(), format),
    exportName("inscrieri", format), null, "Export pornit.");

  const clear = () => {
    setQDraft("");
    setFilters({
      q: "", status: "", crm_stage: "", enrollment_mode: "", email: "",
      friendly_source: "", consent_marketing: "",
      county: "", specializare: "", ministry_status: "", regime: "", referral: "",
      date_from: "", date_to: "",
      include_deleted: false, test_only: false, expired_only: false,
    });
  };

  useEffect(() => {
    if (!seesContracts || items.length === 0) { setContractsByEnrollment({}); return undefined; }
    let alive = true;
    api.post("/admin/contracts/lookup", { enrollment_ids: items.map((i) => i.id) })
      .then((r) => { if (alive) setContractsByEnrollment(r.data.contracts || {}); })
      .catch(() => { if (alive) setContractsByEnrollment({}); });
    return () => { alive = false; };
  }, [items, seesContracts]);

  const toggleRow = (id) => setSelected((p) => {
    const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleAll = () => setSelected(selected.size === items.length ? new Set() : new Set(items.map(i => i.id)));

  const updateCrmStage = async (enrollmentId, stage) => {
    try {
      const r = await api.patch(`/admin/enrollments/${enrollmentId}`, { crm_stage: stage });
      setItems((prev) => prev.map((it) => (it.id === enrollmentId ? { ...it, ...r.data } : it)));
      toast.success("Etapă CRM actualizată");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const requestDelete = (ids) => {
    if (!isOwner || ids.length === 0) return;
    setDeleteConfirm({ ids, reason: "", confirm_paid_live: false, retry409: false });
  };
  const doDelete = async () => {
    const { ids, reason, confirm_paid_live } = deleteConfirm;
    try {
      if (ids.length === 1) {
        await api.delete(`/admin/enrollments/${ids[0]}`, { data: { reason, confirm_paid_live } });
      } else {
        await api.post("/admin/enrollments/bulk-delete", { ids, reason, confirm_paid_live });
      }
      toast.success(`${ids.length} înscriere${ids.length === 1 ? "" : "/-i"} ștearsă`);
      setDeleteConfirm(null);
      setSelected(new Set());
      load();
    } catch (err) {
      if (err?.response?.status === 409) {
        setDeleteConfirm((d) => ({ ...d, retry409: true }));
        toast.message(err?.response?.data?.detail || "Confirmare necesară");
        return;
      }
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const restore = async (e) => {
    try {
      await api.post(`/admin/enrollments/${e.id}/restore`);
      toast.success("Restaurată");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    }
  };

  const filtersActive = Boolean(
    filters.q || filters.status || filters.crm_stage || filters.enrollment_mode ||
    filters.email || filters.friendly_source || filters.consent_marketing ||
    filters.county || filters.specializare || filters.ministry_status || filters.regime || filters.referral ||
    filters.date_from || filters.date_to
  );

  return (
    <div className="space-y-4" data-testid="enrollments-tab">
      <EnrollmentsFilterBar
        filters={filters}
        setFilters={setFilters}
        qDraft={qDraft}
        setQDraft={setQDraft}
        isOwner={isOwner}
        currentRole={currentRole}
        onReload={load}
        onExport={exportCsv}
        onExportMinistry={exportMinistryCsv}
        onClear={clear}
        filtersActive={filtersActive}
        expiredTotal={expiredTotal}
      />

      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm" data-testid="enrollments-table">
          <thead className="bg-[#EDEEF0] text-[#373A45] sticky top-0 z-10">
            <tr className="text-left">
              {isOwner && (
                <th className="px-3 py-3 w-8">
                  <button onClick={toggleAll} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Selectează tot">
                    {items.length > 0 && selected.size === items.length ? <CheckSquare size={14} className="text-[#965EA7]" /> : <Square size={14} />}
                  </button>
                </th>
              )}
              <Th>Nume</Th>
              <Th>Email</Th>
              <Th>Județ</Th>
              <Th>Status</Th>
              <Th>Etapă CRM</Th>
              <Th>Canal</Th>
              <Th>Înregistrată</Th>
              <Th>Atribuit lui</Th>
              <Th>Date minister</Th>
              {seesContracts && <Th>Contract</Th>}
              {isOwner && <th className="px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={13} className="p-6 text-center text-[#6B7280]">Se încarcă…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={13} className="p-6 text-center text-[#6B7280]">Nicio înscriere pentru filtrele curente.</td></tr>
            )}
            {!loading && items.map((e) => {
              const st = STATUS_BY[e.status] || { label: e.status, cls: "bg-[#6B7280]/15" };
              const fs = e.friendly_source || "Unknown";
              const fsCls = FRIENDLY_SOURCE_TONE[fs] || "bg-[#6B7280]/10 text-[#6B7280]";
              const stage = CRM_BY[e.crm_stage] || CRM_STAGES[0];
              return (
                <tr key={e.id}
                    className={`border-t border-[#E5E7EB] hover:bg-[#F5F6F6] cursor-pointer ${selected.has(e.id) ? "bg-[#F5F0F8]" : ""}`}
                    onClick={() => setDrawerId(e.id)}
                    data-testid={`enrollment-row-${e.id}`}>
                  {isOwner && (
                    <td className="px-3 py-3 w-8" onClick={(ev) => ev.stopPropagation()}>
                      <button onClick={() => toggleRow(e.id)} className="text-[#6B7280] hover:text-[#373A45]" data-testid={`enr-checkbox-${e.id}`}>
                        {selected.has(e.id) ? <CheckSquare size={14} className="text-[#965EA7]" /> : <Square size={14} />}
                      </button>
                    </td>
                  )}
                  <Td>
                    <div className="flex flex-col leading-tight">
                      <span className="font-medium text-[#373A45]">
                        {e.prenume} {e.nume}
                        {e.source === "lead_conversion" && (
                          <span
                            className="ml-1.5 inline-flex items-center rounded-full bg-[#965EA7]/12 text-[#7A4986] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest"
                            title="Creată din conversia unui lead"
                            data-testid={`enr-source-lead-${e.id}`}
                          >
                            din lead
                          </span>
                        )}
                        {e.resubmitted_at && (
                          <span
                            className="ml-1.5 inline-flex items-center rounded-full bg-[#965EA7]/15 text-[#7A4986] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest"
                            title="Persoana a completat formularul din nou — vezi în fișă ce a introdus diferit"
                            data-testid={`enr-resubmitted-${e.id}`}
                          >
                            reînscriere
                          </span>
                        )}
                        {isFped(e) && (
                          <span
                            className="ml-1.5 inline-flex items-center rounded-full bg-[#3F8EBA]/10 text-[#2f6d90] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest"
                            title="Grupă în afara acreditării: fără registrul formabililor, cu contract de participare la curs"
                            data-testid={`enr-regime-${e.id}`}
                          >
                            Curs FPED
                          </span>
                        )}
                        {e.needs_group_assignment && (
                          <span
                            className="ml-1.5 inline-flex items-center rounded-full bg-[#FDBA74]/30 text-[#92400E] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest"
                            title="Înscriere trimisă fără grupă — atribuie manual"
                            data-testid={`enr-needs-group-${e.id}`}
                          >
                            fără grupă
                          </span>
                        )}
                      </span>
                      <span className="text-[11px] text-[#6B7280]">{e.specializare || "—"}</span>
                    </div>
                  </Td>
                  <Td>{e.email}</Td>
                  <Td>
                    {e.county ? (
                      <span className="inline-flex items-center rounded-full bg-[#EDEEF0] text-[#373A45] px-2 py-0.5 text-[11px] font-semibold">
                        {e.county}
                      </span>
                    ) : <span className="text-[#6B7280]">—</span>}
                  </Td>
                  <Td>
                    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${st.cls}`}>
                      {st.label}
                    </span>
                  </Td>
                  <Td>
                    <CrmStageBadge
                      value={stage}
                      disabled={!canMutate}
                      onChange={(v) => updateCrmStage(e.id, v)}
                      testId={`enr-crm-stage-${e.id}`}
                    />
                  </Td>
                  <Td>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${fsCls}`}
                      data-testid={`enr-channel-${e.id}`}
                    >
                      {fs}
                    </span>
                  </Td>
                  <Td>
                    <span className="text-xs text-[#6B7280]">
                      {new Date(e.created_at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </Td>
                  <Td>
                    {e.assigned_to_email ? (
                      <span
                        className="inline-flex items-center rounded-full bg-[#519872]/15 text-[#2F6B4E] px-2 py-0.5 text-[11px] font-medium"
                        title={e.assigned_to_email}
                        data-testid={`enr-assigned-${e.id}`}
                      >
                        {e.assigned_to_email.split("@")[0]}
                      </span>
                    ) : (
                      <span className="text-xs text-[#6B7280] italic" data-testid={`enr-unassigned-${e.id}`}>Neatribuit</span>
                    )}
                  </Td>
                  <Td>
                    {(() => {
                      const m = MINISTRY_BY[e.ministry_status] || MINISTRY_BY.missing;
                      return (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${m.cls}`}
                            data-testid={`enr-ministry-${e.id}`}
                          >
                            {m.label}
                          </span>
                          {/* The check digit did not add up. Not a verdict —
                              it catches most typos, not all — but this is the
                              shortlist to eyeball before the dossier goes in,
                              instead of a warning one person saw once. */}
                          {e.ministry_cnp_suspect && (
                            <AlertTriangle
                              size={13}
                              className="text-[#B45309] shrink-0"
                              aria-label="CNP de verificat"
                              data-testid={`enr-ministry-suspect-${e.id}`}
                            />
                          )}
                        </span>
                      );
                    })()}
                  </Td>
                  {seesContracts && (
                    <Td>
                      {(() => {
                        const c = contractsByEnrollment[e.id];
                        if (!c) return <span className="text-xs text-[#6B7280]">—</span>;
                        const st = shortStatus(c.status);
                        return (
                          <span className="flex flex-col leading-tight" data-testid={`enr-contract-${e.id}`}>
                            <span className={`inline-flex w-fit whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${CONTRACT_TONE[st.tone]}`}>
                              {st.label}
                            </span>
                            <span className="mt-0.5 font-mono text-[10px] text-[#6B7280]">{c.number}</span>
                          </span>
                        );
                      })()}
                    </Td>
                  )}
                  {isOwner && (
                    <td className="px-4 py-3 text-right" onClick={(ev) => ev.stopPropagation()}>
                      {e.deleted_at ? (
                        <>
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#6B7280]/15 text-[#373A45] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest mr-2">Ștearsă</span>
                          <button onClick={() => restore(e)} className="text-[#519872] hover:underline text-xs inline-flex items-center gap-1" data-testid={`enr-restore-${e.id}`}>
                            <RotateCcw size={12} />
                          </button>
                        </>
                      ) : (
                        <button onClick={() => requestDelete([e.id])} className="text-[#9b1f2c] hover:underline text-xs inline-flex items-center gap-1" data-testid={`enr-delete-${e.id}`}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pageMeta.page}
        pageSize={pageMeta.page_size}
        total={total}
        totalPages={pageMeta.total_pages}
        hasNext={pageMeta.has_next}
        hasPrev={pageMeta.has_prev}
        onPageChange={setPage}
        testid="enrollments-pagination"
      />

      <div className="text-xs text-[#6B7280] px-1">
        Total: {total} înscrieri · {items.length} afișate
        {selected.size > 0 && <span className="ml-2 text-[#965EA7] font-semibold">· {selected.size} selectate</span>}
      </div>

      {isOwner && selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#373A45] text-white rounded-full px-3 py-2 shadow-2xl flex items-center gap-2"
             data-testid="enrollments-bulk-bar">
          <span className="text-xs font-semibold pl-3">{selected.size} selectate</span>
          <button
            onClick={() => requestDelete(Array.from(selected))}
            className="rounded-full bg-[#9b1f2c] hover:bg-[#7e1923] px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1"
            data-testid="enrollments-bulk-delete"
          >
            <Trash2 size={12} /> Șterge
          </button>
          <button onClick={() => setSelected(new Set())} className="rounded-full px-3 py-1.5 text-xs text-white/80 hover:text-white">
            <X size={14} />
          </button>
        </div>
      )}

      {drawerId && <EnrollmentDrawer enrollmentId={drawerId} onClose={() => setDrawerId(null)} onChanged={load} canMutate={canMutate} currentRole={currentRole} />}
      {deleteConfirm && <DeleteModal state={deleteConfirm} onChange={setDeleteConfirm} onCancel={() => setDeleteConfirm(null)} onConfirm={doDelete} />}
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold whitespace-nowrap">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 text-[#373A45] align-top ${className}`}>{children}</td>;
}

function EnrollmentDrawer({ enrollmentId, onClose, onChanged, canMutate, currentRole }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPaymentLink, setShowPaymentLink] = useState(false);
  const [groups, setGroups] = useState([]);
  const [newGroupId, setNewGroupId] = useState("");
  // Payment modal state (M720): amount + internal note + chosen mode
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [sitePrice, setSitePrice] = useState(975);
  // "Standard" for this enrolment: its cohort's own price, kept when it
  // enrolled (09.10.2026), else the site's.
  const standardPrice = Number(data?.group_price) > 0 ? Number(data.group_price) : sitePrice;
  // Snapshot of payment toggles used to gate the "Trimite link de plată"
  // action. Mirrors the same three gates the backend now enforces (env keys,
  // admin toggle, checkout_mode). Fetched once when the drawer opens — admin
  // settings change rarely and a single fetch keeps the UI snappy.
  const [paymentGate, setPaymentGate] = useState({
    enabled: false,
    configured: false,
    provider: "Plățile online",
    checkout_mode: "registration_only",
  });
  // Full edit-mode (M675): toggle the drawer between read-only details and a
  // form that mirrors every editable field. `form` holds the working copy
  // while editing; `editMode=false` keeps the legacy quick-action UI intact.
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(null);
  // After a move: the question about the new programme, with the fresh record
  // it will be sent for. Never a background send (28.09.2026).
  const [groupEmailAsk, setGroupEmailAsk] = useState(null);

  useEffect(() => {
    api.get(`/admin/enrollments/${enrollmentId}`).then((r) => {
      setData(r.data);
      setNewGroupId(r.data.group_id || "");
    }).catch(() => {});
    api.get("/admin/groups").then((r) => {
      setGroups((r.data || []).filter((g) => !g.archived && g.status === "active"));
    }).catch(() => {});
    api.get("/admin/settings").then((r) => {
      setPaymentGate({
        // The ACTIVE provider, whatever it is called — the site runs on
        // EuPlătesc, and asking about Netopia disabled the button wrongly.
        enabled: !!(r.data?.payments_enabled ?? r.data?.netopia_enabled),
        configured: !!(r.data?.payment_provider_configured ?? r.data?.netopia_configured),
        provider: r.data?.payment_provider_display_name || "Plățile online",
        checkout_mode: r.data?.checkout_mode || "registration_only",
      });
      const sp = Number(r.data?.price) || 975;
      setSitePrice(sp);
    }).catch(() => {});
  }, [enrollmentId]);

  // Initialize the editable form snapshot from `data` whenever we enter edit
  // mode or the underlying record refreshes (e.g., after save).
  const enterEdit = () => {
    if (!data) return;
    const tp = coerceLegacyTeacherProfile(data);
    setForm({
      nume: data.nume || "",
      prenume: data.prenume || "",
      email: data.email || "",
      telefon: data.telefon || "",
      specializare: data.specializare || "",
      teaching_levels: tp.teaching_levels,
      teaching_level_other: tp.teaching_level_other,
      specialization: tp.specialization,
      specialization_other: tp.specialization_other,
      unitate: data.unitate || "",
      city: data.city || "",
      county: data.county || "",
      uat_name: data.uat_name || "",
      school_code: data.school_code || null,
      city_original: data.city_original || "",
      manual_school_entry: !!data.manual_school_entry,
      group_id: data.group_id || "",
      status: data.status || "submitted",
      crm_stage: data.crm_stage || "new",
      preferred_course_days: data.preferred_course_days || "",
      preferred_course_days_other: data.preferred_course_days_other || "",
      preferred_time_of_day: data.preferred_time_of_day || "",
      preferred_time_of_day_other: data.preferred_time_of_day_other || "",
    });
    setEditMode(true);
  };
  const cancelEdit = () => {
    setEditMode(false);
    setForm(null);
  };

  // Build the diff to send. We only PATCH what actually changed — same shape
  // the backend uses for its `{old, new}` audit entries.
  const computeDirty = () => {
    if (!form || !data) return {};
    const out = {};
    const fields = [
      "nume", "prenume", "email", "telefon", "specializare", "unitate",
      "city", "county", "status", "crm_stage",
      "preferred_course_days", "preferred_course_days_other",
      "preferred_time_of_day", "preferred_time_of_day_other",
    ];
    fields.forEach((f) => {
      const cur = data[f] || "";
      const nx = form[f] || "";
      if (cur !== nx) out[f] = form[f];
    });
    const curGid = data.group_id || "";
    const nxGid = form.group_id || "";
    if (curGid !== nxGid) out.group_id = form.group_id || null;
    return out;
  };
  const isDirty = editMode && Object.keys(computeDirty()).length > 0;

  // A new cohort for somebody already at Logiscool: ask, then move them there
  // too (decided 22.09.2026 — with confirmation, each time). Each mode apart.
  const offerLimoMove = async (fresh) => {
    for (const sandbox of [false, true]) {
      if (!(sandbox ? fresh?.limo_test_student_id : fresh?.limo_student_id)) continue;
      try {
        const st = await api.get(`/admin/enrollments/${enrollmentId}/limo`, { params: { sandbox } });
        const move = st.data?.move;
        if (move?.blocked) { toast.warning(`Logiscool${sandbox ? " (probă)" : ""}: ${move.blocked}`); continue; }
        if (!move?.needed) continue;
        if (!window.confirm(`${sandbox ? "[Probă] " : ""}${moveQuestion(move)}`)) {
          toast.message("Nu l-am mutat la Logiscool. Îl poți muta oricând din secțiunea Logiscool a fișei.");
          continue;
        }
        const r = await api.post(`/admin/enrollments/${enrollmentId}/limo/move`, null, { params: { sandbox } });
        if (r.data.partial) toast.warning(r.data.message); else toast.success(r.data.message);
      } catch (err) {
        const d = err?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Mutarea la Logiscool nu a reușit.");
      }
    }
  };

  // The new programme, after a move. Offered for somebody who holds a place;
  // for anybody else the reason is said out loud rather than nothing.
  const offerGroupEmail = (fresh) => {
    if (canNotifyGroupChange(fresh)) {
      setGroupEmailAsk(fresh);
      return;
    }
    toast.message(`Nu am oferit emailul cu noul program. ${whyNotNotify(fresh)}`);
  };

  const sendGroupEmail = async () => {
    setGroupEmailAsk(null);
    setBusy(true);
    try {
      const r = await api.post(`/admin/enrollments/${enrollmentId}/notify-group-change`);
      toast.success(r.data?.message || "Emailul cu noul program a plecat.");
      const fresh = await api.get(`/admin/enrollments/${enrollmentId}`);
      setData(fresh.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Emailul cu noul program nu a plecat.");
    } finally {
      setBusy(false);
    }
  };

  // More time for somebody who promised to pay, or no deadline at all: the
  // team's call, not a timer's (23.09.2026).
  const setDeadline = async (params) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/enrollments/${enrollmentId}/payment-deadline`, null, { params });
      setData({ ...data, payment_deadline: r.data.payment_deadline });
      toast.success(params.hold === true ? "Înscrierea nu mai expiră automat."
        : params.hold === false ? "Termenul de plată a fost pus la loc."
        : "Termenul de plată a fost prelungit.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nu am putut schimba termenul.");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    const dirty = computeDirty();
    if (Object.keys(dirty).length === 0) {
      toast.message("Nimic de salvat — nicio modificare detectată.");
      return;
    }
    setBusy(true);
    // Optimistic update: paint the new values immediately, revert if backend rejects.
    const prev = data;
    setData({ ...data, ...dirty });
    try {
      const r = await api.patch(`/admin/enrollments/${enrollmentId}`, dirty);
      setData(r.data);
      toast.success("Înscrierea a fost actualizată.");
      setEditMode(false);
      setForm(null);
      if ("group_id" in dirty) {
        await offerLimoMove(r.data);
        offerGroupEmail(r.data);
      }
      onChanged?.();
    } catch (err) {
      setData(prev);  // rollback
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Eroare la salvare");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (newStatus) => {
    setBusy(true);
    try {
      await api.patch(`/admin/enrollments/${enrollmentId}`, { status: newStatus });
      toast.success("Status actualizat");
      const r = await api.get(`/admin/enrollments/${enrollmentId}`);
      setData(r.data);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    } finally {
      setBusy(false);
    }
  };

  const setCrm = async (stage) => {
    setBusy(true);
    try {
      await api.patch(`/admin/enrollments/${enrollmentId}`, { crm_stage: stage });
      toast.success("Etapă CRM actualizată");
      const r = await api.get(`/admin/enrollments/${enrollmentId}`);
      setData(r.data);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare");
    } finally {
      setBusy(false);
    }
  };

  // Read the most recent current purchase (if any) — used to default the
  // amount field + decide if a reminder button should appear.
  const currentPurchase = data?.payment || null;
  const hasActiveLink = !!currentPurchase
    && ["pending", "pending_payment"].includes(currentPurchase.status);

  const openPaymentModal = () => {
    // Default amount: last current purchase amount (if any) → else standard
    // price from settings. Always show 2 decimals when truncated.
    const fallback = currentPurchase?.amount ?? standardPrice;
    setPaymentAmount(String(fallback));
    setPaymentNote("");
    setShowPaymentLink(true);
  };

  const sendPaymentLink = async (mode = "send_link") => {
    const amt = Number(paymentAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Suma trebuie să fie un număr strict pozitiv.");
      return;
    }
    setBusy(true);
    try {
      const body = {
        amount: amt,
        currency: "RON",
        mode,
        internal_note: paymentNote.trim() || undefined,
      };
      if (newGroupId && newGroupId !== data.group_id) body.group_id = newGroupId;
      const r = await api.post(
        `/admin/enrollments/${enrollmentId}/send-payment-link`,
        body,
      );
      const action = r.data.action || mode;
      const labels = {
        payment_link_created: "Link nou de plată trimis",
        payment_link_resent: "Link de plată retrimis",
        payment_reminder_sent: "Reminder de plată trimis",
      };
      toast.success(`${labels[action] || "Email trimis"} către ${r.data.email_sent_to}`);
      setShowPaymentLink(false);
      const fresh = await api.get(`/admin/enrollments/${enrollmentId}`);
      setData(fresh.data);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la trimitere");
    } finally {
      setBusy(false);
    }
  };

  const assignGroup = async () => {
    if (!newGroupId) {
      toast.error("Alege o grupă din listă");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/admin/enrollments/${enrollmentId}/assign-group`,
        { group_id: newGroupId }
      );
      toast.success("Grupă atribuită");
      setData(r.data);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la atribuire");
    } finally {
      setBusy(false);
    }
  };

  // Drawer-level enabledness for the "Trimite link de plată" action. Mirrors
  // the backend gates: Netopia configured + admin toggle ON. INDEPENDENT of
  // `checkout_mode` — sending a payment link from the dashboard works even
  // when the public checkout is in `registration_only` mode (that's the
  // whole point of the "collect leads, send pay links later" workflow).
  const paymentsAvailable = paymentGate.configured && paymentGate.enabled;
  const paymentDisabledReason = (() => {
    if (!paymentGate.configured) return `${paymentGate.provider} nu este configurat în backend (cheile lipsesc).`;
    if (!paymentGate.enabled) return "Activează plățile online din Admin → Setări curs pentru a putea trimite linkuri de plată.";
    return "";
  })();
  // M720: payment link is permitted across the full lifecycle EXCEPT for
  // terminal states (paid, refunded, deleted, duplicate). The previous version
  // only allowed submitted/confirmed — too restrictive: operators routinely
  // need to retry abandoned/failed/cancelled enrollments.
  const PAYMENT_ALLOWED_STATUSES = [
    "submitted", "confirmed",
    "pending_payment", "abandoned", "failed", "cancelled",
  ];
  const canSendPaymentLink = canMutate && data
    && PAYMENT_ALLOWED_STATUSES.includes(data.status);
  const isPaymentLocked = data?.status === "paid" || data?.status === "refunded";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="enrollment-drawer">
      <aside
        className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[#E5E7EB] flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="font-display text-xl font-bold text-[#373A45] truncate">
              {editMode ? "Editează înscrierea" : "Detalii înscriere"}
            </h3>
            {data?.manually_edited_at && !editMode && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-[#F0E7F4] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#7A4986]"
                title={`Modificat de ${data.manually_edited_by || "—"} la ${new Date(data.manually_edited_at).toLocaleString("ro-RO")}`}
                data-testid="enr-manual-edit-badge"
              >
                <History size={11} /> Modificat manual
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {data && canMutate && !editMode && (
              <button
                onClick={enterEdit}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E7EB] px-3 py-1.5 text-xs font-semibold text-[#373A45] hover:bg-[#F5F6F6]"
                data-testid="enr-edit-button"
              >
                <Pencil size={13} /> Editează înscrierea
              </button>
            )}
            <button onClick={onClose} className="text-[#6B7280] hover:text-[#373A45]" data-testid="enr-drawer-close"><X size={18} /></button>
          </div>
        </div>
        {!data ? (
          <div className="p-6 text-[#6B7280]">Se încarcă…</div>
        ) : editMode ? (
          <EditForm
            form={form}
            setForm={setForm}
            groups={groups}
            data={data}
            busy={busy}
          />
        ) : (
          <div className="p-6 space-y-4 text-sm overflow-y-auto flex-1">
            {data.registration_number && (
              <Field label="Nr. înregistrare cerere" value={data.registration_number} />
            )}
            <Field label="Nume complet" value={`${data.prenume} ${data.nume}`} />
            <Field label="Email" value={data.email} />
            {["owner", "admin", "admin_fped"].includes(currentRole) && (
              <PortalEmailField enrollmentId={data.id} value={data.portal_email} />
            )}
            <Field label="Telefon" value={data.telefon} />
            <Field
              label="Specializare"
              value={
                <span className="inline-flex items-center gap-1.5 flex-wrap">
                  <span>{data.specialization || data.specializare || "—"}</span>
                  {data.specialization === "Altă disciplină" && data.specialization_other && (
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#FFF7E6] text-[#7A5A1B] border border-[#F5C76E]/40" title="Disciplină introdusă manual">
                      {data.specialization_other}
                    </span>
                  )}
                </span>
              }
            />
            <Field
              label="Ciclu de învățământ"
              value={
                Array.isArray(data.teaching_levels) && data.teaching_levels.length ? (
                  <span className="flex flex-wrap gap-1">
                    {data.teaching_levels.map((lvl) => (
                      <span key={lvl} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F3E8F7] text-[#965EA7] font-medium">
                        {lvl}
                      </span>
                    ))}
                    {data.teaching_levels.includes("Altul") && data.teaching_level_other && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#FFF7E6] text-[#7A5A1B] border border-[#F5C76E]/40">
                        {data.teaching_level_other}
                      </span>
                    )}
                  </span>
                ) : "—"
              }
            />
            <Field
              label="Unitate de învățământ"
              value={
                <span className="inline-flex items-center gap-2">
                  <span>{data.unitate || "—"}</span>
                  {data.manual_school_entry && (
                    <span
                      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFF7E6] text-[#7A5A1B] border border-[#F5C76E]/40"
                      title="Unitate introdusă manual (nu există în catalogul oficial)"
                    >
                      manual
                    </span>
                  )}
                  {data.school_code && (
                    <span className="text-[10px] font-mono text-[#6B7280]" title="Cod școală">#{data.school_code}</span>
                  )}
                </span>
              }
            />
            <Field label="Localitate (UAT)" value={data.uat_name || data.city || "—"} />
            {data.city_original && data.city_original !== (data.uat_name || data.city) && (
              <Field label="Localitate exactă (din catalog)" value={data.city_original} />
            )}
            <Field label="Județ" value={data.county || "—"} />
            <Field
              label="Grupă"
              value={
                data.group_snapshot?.name ? (
                  <span className="flex flex-col">
                    <span>
                      {data.group_snapshot.name}
                      {isFped(data) && (
                        <span className="ml-2 inline-flex rounded-full bg-[#3F8EBA]/10 px-2 py-0.5 text-[10px] font-semibold text-[#2f6d90]"
                          data-testid="enr-drawer-regime">Curs FPED</span>
                      )}
                    </span>
                    {data.group_snapshot.schedule_label && (
                      <span className="text-xs text-[#6B7280]">{data.group_snapshot.schedule_label}</span>
                    )}
                    <GroupSessionsList sessions={data.group_snapshot.sessions} testid="enr-group-sessions" className="mt-1" />
                    {canNotifyGroupChange(data) && (
                      <span className="mt-1 inline-flex flex-wrap items-center gap-2">
                        <button type="button" disabled={busy} onClick={() => setGroupEmailAsk(data)}
                          data-testid="enr-send-group-programme"
                          className="inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] px-2.5 py-1 text-[11px] font-semibold text-[#7A4986] hover:bg-[#F5F0F8] disabled:opacity-60">
                          <Send size={11} /> Trimite programul pe email
                        </button>
                        {data.group_change_notified_at && (
                          <span className="text-[11px] text-[#6B7280]">
                            trimis {new Date(data.group_change_notified_at).toLocaleDateString("ro-RO")}
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                ) : (data.group_id || (data.needs_group_assignment ? "Necesită atribuire" : "—"))
              }
            />
            {(data.preferred_course_days || data.preferred_time_of_day) && (
              <div
                className="rounded-xl border border-[#E5E7EB] bg-[#FAFAFB] p-3 space-y-2"
                data-testid="enr-preferences-block"
              >
                <div className="text-[11px] font-semibold uppercase tracking-widest text-[#6B7280]">
                  Preferințe grupă viitoare
                </div>
                {data.preferred_course_days && (
                  <Field
                    label="Zile preferate"
                    value={
                      <span data-testid="enr-preferred-days-value">
                        {data.preferred_course_days === "Altă variantă" && data.preferred_course_days_other
                          ? `Altă variantă — ${data.preferred_course_days_other}`
                          : data.preferred_course_days}
                      </span>
                    }
                  />
                )}
                {data.preferred_time_of_day && (
                  <Field
                    label="Parte a zilei"
                    value={
                      <span data-testid="enr-preferred-time-value">
                        {data.preferred_time_of_day === "Altă variantă" && data.preferred_time_of_day_other
                          ? `Altă variantă — ${data.preferred_time_of_day_other}`
                          : data.preferred_time_of_day}
                      </span>
                    }
                  />
                )}
              </div>
            )}
            {data.needs_group_assignment && canMutate && (
              <div
                className="rounded-xl border border-[#FDBA74] bg-[#FFF7ED] p-4"
                data-testid="enr-assign-group-card"
              >
                <div className="font-semibold text-[#92400E] text-xs uppercase tracking-widest mb-2">
                  Atribuie grupă
                </div>
                <p className="text-xs text-[#92400E]/90 mb-3">
                  Această înscriere a fost trimisă fără grupă. Alege o cohortă activă pentru a o atribui.
                </p>
                <div className="flex items-center gap-2">
                  <select
                    value={newGroupId}
                    onChange={(e) => setNewGroupId(e.target.value)}
                    className="flex-1 rounded-lg border border-[#FDBA74] bg-white px-3 py-2 text-sm text-[#373A45]"
                    data-testid="enr-assign-group-select"
                  >
                    <option value="">— alege grupa —</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}{g.start_date ? ` · ${new Date(g.start_date).toLocaleDateString("ro-RO")}` : ""}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={assignGroup}
                    disabled={busy || !newGroupId}
                    className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-50 text-white px-4 py-2 text-sm font-semibold whitespace-nowrap"
                    data-testid="enr-assign-group-button"
                  >
                    Atribuie
                  </button>
                </div>
              </div>
            )}
            <Field label="Mod" value={data.enrollment_mode} />
            <Field label="Status curent" value={data.status} />
            <Field label="Etapă CRM" value={CRM_BY[data.crm_stage]?.label || data.crm_stage || "—"} />
            <Field label="Canal" value={data.friendly_source || "—"} />
            <Field label="Sursă" value={data.source} />
            <Field label="Consent marketing" value={data.consent_marketing ? "Da" : "Nu"} />
            <Field label="Înregistrată la" value={new Date(data.created_at).toLocaleString("ro-RO")} />

            {/* M918 — Attribution / UTM block. Always shown so we can see
                "Direct" as well, but each value falls back to em-dash when
                missing. */}
            <div className="md:col-span-2 rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] p-4 mt-2" data-testid="enr-utm-block">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[#6B7280] mb-2">
                Atribuire (UTM + click IDs)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <Field label="UTM source"   value={data.utm?.utm_source   || "—"} />
                <Field label="UTM medium"   value={data.utm?.utm_medium   || "—"} />
                <Field label="UTM campaign" value={data.utm?.utm_campaign || "—"} />
                <Field label="UTM content"  value={data.utm?.utm_content  || "—"} />
                <Field label="UTM term"     value={data.utm?.utm_term     || "—"} />
                <Field label="fbclid"       value={data.fbclid || "—"} />
                <Field label="gclid"        value={data.gclid  || "—"} />
                <Field label="Referrer"     value={data.referrer || "—"} />
                <Field label="Landing page" value={data.landing_page || "—"} />
              </div>
            </div>
            <div className="md:col-span-2 pt-2 pb-1">
              <AssignSelector
                endpoint={`enrollments/${enrollmentId}/assign`}
                docId={enrollmentId}
                currentAssignedToUserId={data.assigned_to_user_id}
                currentAssignedToEmail={data.assigned_to_email}
                canManage={currentRole === "owner" || currentRole === "admin_fped" || currentRole === "admin"}
                onChanged={(res) => {
                  setData((prev) => prev ? { ...prev, ...res } : prev);
                  onChanged?.();
                }}
                testIdPrefix="enr-assign"
              />
            </div>
            {/* Accreditation dossier. Above the timeline because when this
                drawer is opened during a phone call, this is the reason. */}
            <MinistryDataPanel enrollmentId={enrollmentId} canMutate={canMutate} fped={isFped(data)} />
            {/* The initial assessment (15.10.2026): the accredited course only; the answers are about the
                person, so owner and admin, as on the server. */}
            {!isFped(data) && ["owner", "admin_fped", "admin"].includes(currentRole) && (
              <EnrollmentAssessmentPanel enrollmentId={enrollmentId} canMutate={canMutate}
                name={`${data.prenume || ""} ${data.nume || ""}`.trim()} email={data.email} />
            )}
            {/* Owner and admin only: the endpoints behind it show passwords and
                spend licences, and a button that answers 403 is worse than no
                button. */}
            {(currentRole === "owner" || currentRole === "admin_fped" || currentRole === "admin") && (
              <LimoEnrollmentPanel enrollmentId={enrollmentId} canMutate={canMutate} />
            )}
            {/* Owner and admin only, for the same reason: the preview carries
                the participant's CNP. */}
            {(currentRole === "owner" || currentRole === "admin_fped" || currentRole === "admin") && (
              <ContractPanel enrollmentId={enrollmentId} />
            )}
            {/* The referral code; the button only for who the server lets send it. */}
            <ReferralCodePanel
              enrollmentId={enrollmentId}
              canSend={canMutate && (currentRole === "owner" || currentRole === "admin_fped" || currentRole === "admin")}
            />
            <ActivityTimeline
              enrollmentId={enrollmentId}
              canMutate={canMutate}
              refreshKey={data.updated_at /* refetch on any patch/note */}
            />
            {data.payment && (
              <div className="rounded-xl border border-[#E5E7EB] p-4 bg-[#F5F6F6]" data-testid="enr-payment-card">
                <div className="font-semibold text-[#373A45] mb-2 flex items-center gap-1">
                  Plată asociată <ExternalLink size={12} />
                  {data.payment.custom_amount && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#FFEDD5] text-[#9A3412] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
                      data-testid="enr-payment-custom-badge"
                      title={data.payment.custom_amount_reason || "Sumă diferită de prețul standard"}
                    >
                      Sumă personalizată
                    </span>
                  )}
                  {(data.payment.payment_attempt_number || 1) > 1 && (
                    <span
                      className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#E0E7FF] text-[#3730A3] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
                      data-testid="enr-payment-attempt-badge"
                    >
                      Tentativa {data.payment.payment_attempt_number}
                    </span>
                  )}
                </div>
                <div className="text-xs space-y-1">
                  <div>Status: <strong>{data.payment.status}</strong></div>
                  <div>Sumă: {Number(data.payment.amount).toFixed(2)} {data.payment.currency}</div>
                  <div>Provider: {data.payment.provider}</div>
                  {data.payment.netopia_order_id && <div>Order: {data.payment.netopia_order_id}</div>}
                  {data.payment.netopia_ntp_id && <div>NTP ID: {data.payment.netopia_ntp_id}</div>}
                  {data.payment.euplatesc_invoice_id && (
                    <div>Nr. comandă EuPlătesc: <span className="font-mono">{data.payment.euplatesc_invoice_id}</span></div>
                  )}
                  {data.payment.custom_amount_reason && (
                    <div className="text-[#6B7280] italic">
                      Notă internă: „{data.payment.custom_amount_reason}"
                    </div>
                  )}
                  {data.payment.previous_payment_id && (
                    <div className="text-[10px] text-[#6B7280]">
                      Înlocuiește plata anterioară #{data.payment.previous_payment_id.slice(0, 8)}
                    </div>
                  )}
                </div>
                <PaymentFailureBox payment={data.payment} />
              </div>
            )}
            {data.lead && (
              <div className="rounded-xl border border-[#E5E7EB] p-4 bg-[#F5F0F8]">
                <div className="font-semibold text-[#373A45] mb-2">Lead asociat</div>
                <div className="text-xs">Email: {data.lead.email} · Status: {data.lead.status}</div>
              </div>
            )}
            {isPaymentLocked && (
              <div
                className="rounded-xl border border-[#86EFAC] bg-[#F0FDF4] px-3 py-2 text-xs text-[#166534] flex items-center gap-2"
                data-testid="enr-payment-locked"
              >
                <strong>{data.status === "paid" ? "Plată confirmată" : "Înscriere rambursată"}.</strong>
                {data.status === "paid" && " Această înscriere este deja plătită — linkul de plată nu mai poate fi trimis."}
              </div>
            )}
            {data.status === "pending_payment" && (() => {
              const note = deadlineNote(data.payment_deadline, new Date(), data.payment_deadline_hours);
              if (!note) return null;
              const tone = { open: "bg-[#F5F6F6] text-[#373A45]", due: "bg-[#FDF3E7] text-[#8a6a1a]",
                held: "bg-[#EDE3F2] text-[#6B3F7A]" }[note.tone];
              return (
                <div className={`rounded-xl px-3 py-2 text-xs ${tone}`} data-testid="enr-payment-deadline">
                  <span>{note.text}</span>
                  {canMutate && (
                    <span className="ml-2 inline-flex flex-wrap gap-1.5">
                      <button type="button" onClick={() => setDeadline({ days: 3 })} disabled={busy}
                        className="rounded-full border border-[#E5E7EB] bg-white px-2.5 py-0.5 font-semibold hover:bg-[#F5F6F6] disabled:opacity-60"
                        data-testid="enr-deadline-extend">
                        Prelungește cu 3 zile
                      </button>
                      <button type="button" onClick={() => setDeadline({ hold: note.tone !== "held" })} disabled={busy}
                        className="rounded-full border border-[#E5E7EB] bg-white px-2.5 py-0.5 font-semibold hover:bg-[#F5F6F6] disabled:opacity-60"
                        data-testid="enr-deadline-hold">
                        {note.tone === "held" ? "Pune termen la loc" : "Nu expira"}
                      </button>
                    </span>
                  )}
                </div>
              );
            })()}
            {canSendPaymentLink && (
              <div className="border-t border-[#E5E7EB] pt-4">
                {!showPaymentLink ? (
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={openPaymentModal}
                        disabled={!paymentsAvailable}
                        title={paymentsAvailable ? "" : paymentDisabledReason}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white px-4 py-2 text-sm font-semibold disabled:bg-[#9CA3AF] disabled:hover:bg-[#9CA3AF] disabled:cursor-not-allowed"
                        data-testid="enr-send-payment-link-button"
                      >
                        {hasActiveLink ? "Gestionează linkul de plată" : "Trimite link de plată"}
                      </button>
                      {hasActiveLink && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] px-2.5 py-1 text-[11px] font-semibold text-[#92400E]"
                          data-testid="enr-active-payment-badge"
                        >
                          Link activ · tentativa {currentPurchase.payment_attempt_number || 1}
                        </span>
                      )}
                    </div>
                    {!paymentsAvailable && (
                      <div
                        className="mt-2 rounded-lg border border-[#FDBA74] bg-[#FFF7ED] px-3 py-2 text-xs text-[#92400E] leading-relaxed"
                        data-testid="enr-payment-link-disabled-reason"
                      >
                        <strong>Plățile online nu sunt active.</strong>{" "}
                        Activează-le din Setări curs ca să poți trimite linkuri de plată.
                        <div className="mt-1 text-[11px] opacity-80">Motiv: {paymentDisabledReason}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="space-y-3 rounded-2xl border border-[#965EA7]/30 bg-[#F0E7F4] p-4"
                    data-testid="enr-payment-modal"
                  >
                    <div className="text-sm font-semibold text-[#7A4986]">
                      {hasActiveLink ? "Gestionează plata" : "Trimite link de plată"}
                    </div>
                    <p className="text-xs text-[#373A45]">
                      Cursantul va primi un email cu linkul de plată. Înscrierea trece la <strong>pending_payment</strong>.
                      Independent de modul checkout-ului public.
                    </p>

                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="text-xs font-semibold uppercase tracking-widest text-[#7A4986]">Suma de plată (RON)</span>
                        <input
                          type="number"
                          step="0.01"
                          min="1"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none"
                          data-testid="enr-payment-amount"
                          autoComplete="off"
                          inputMode="decimal"
                        />
                        <p className="text-[10px] text-[#6B7280] mt-1">
                          {Number(data?.group_price) > 0 ? "Prețul grupei" : "Preț standard"}: {standardPrice.toFixed(2)} RON
                          {Number(paymentAmount) !== standardPrice && Number.isFinite(Number(paymentAmount)) && (
                            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#FFEDD5] text-[#9A3412] px-1.5 py-0.5 font-semibold text-[10px]">
                              sumă personalizată
                            </span>
                          )}
                        </p>
                      </label>
                      <label className="block">
                        <span className="text-xs font-semibold uppercase tracking-widest text-[#7A4986]">Grupa</span>
                        <select
                          value={newGroupId}
                          onChange={(e) => setNewGroupId(e.target.value)}
                          className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm"
                          data-testid="enr-payment-group-select"
                        >
                          <option value="">— păstrează grupa actuală —</option>
                          {groups.map((g) => (
                            <option key={g.id} value={g.id}>{g.name} · {g.start_date}</option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <label className="block">
                      <span className="text-xs font-semibold uppercase tracking-widest text-[#7A4986]">
                        Motiv sumă personalizată / notă internă
                      </span>
                      <input
                        type="text"
                        value={paymentNote}
                        onChange={(e) => setPaymentNote(e.target.value)}
                        placeholder="Ex: discount aprobat telefonic, plată diferență, ofertă instituțională"
                        className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none"
                        data-testid="enr-payment-note"
                        autoComplete="off"
                      />
                      <p className="text-[10px] text-[#6B7280] mt-1">
                        Salvat doar intern (audit + timeline). Cursantul nu vede acest text.
                      </p>
                    </label>

                    <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                      <button
                        onClick={() => setShowPaymentLink(false)}
                        disabled={busy}
                        className="rounded-full px-4 py-2 text-xs border border-[#E5E7EB] bg-white hover:bg-[#F5F6F6]"
                        data-testid="enr-payment-cancel"
                      >
                        Anulează
                      </button>
                      {hasActiveLink && (
                        <button
                          onClick={() => sendPaymentLink("send_reminder")}
                          disabled={busy}
                          className="rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-white px-4 py-2 text-xs font-semibold disabled:opacity-50"
                          data-testid="enr-payment-send-reminder"
                          title="Reia același link și trimite un email de reminder"
                        >
                          Trimite reminder
                        </button>
                      )}
                      <button
                        onClick={() => sendPaymentLink(hasActiveLink ? "regenerate_link" : "send_link")}
                        disabled={busy}
                        className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white px-5 py-2 text-xs font-semibold disabled:opacity-50"
                        data-testid="enr-send-payment-link-confirm"
                      >
                        {busy
                          ? "Se trimite…"
                          : hasActiveLink
                            ? "Regenerează link & trimite"
                            : "Trimite link de plată"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {canMutate && (
              <>
                <div className="border-t border-[#E5E7EB] pt-4">
                  <div className="text-xs font-semibold text-[#373A45] uppercase tracking-widest mb-2">Schimbă etapă CRM</div>
                  <div className="flex flex-wrap gap-2">
                    {CRM_STAGES.map((s) => (
                      <button key={s.v} onClick={() => setCrm(s.v)} disabled={busy || data.crm_stage === s.v}
                              className={`rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-40 ${s.cls}`}
                              data-testid={`enr-set-crm-${s.v}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="border-t border-[#E5E7EB] pt-4">
                  <div className="text-xs font-semibold text-[#373A45] uppercase tracking-widest mb-2">Schimbă status</div>
                  <div className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                      <button key={s.v} onClick={() => setStatus(s.v)} disabled={busy || data.status === s.v}
                              className={`rounded-full px-3 py-1.5 text-xs font-semibold disabled:opacity-40 ${s.cls}`}
                              data-testid={`enr-set-status-${s.v}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {editMode && (
          // Sticky save/cancel bar: stays pinned at the bottom of the drawer,
          // independent of how much content the form contains.
          <div
            className="border-t border-[#E5E7EB] bg-white px-6 py-4 flex items-center justify-between gap-3 shadow-[0_-4px_12px_-6px_rgba(0,0,0,0.08)]"
            data-testid="enr-edit-footer"
          >
            <div className="text-xs text-[#6B7280] truncate">
              {isDirty ? "Modificările nu sunt salvate." : "Nicio modificare detectată."}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={cancelEdit}
                disabled={busy}
                className="rounded-full px-4 py-2 text-xs font-semibold border border-[#E5E7EB] bg-white hover:bg-[#F5F6F6] disabled:opacity-50"
                data-testid="enr-edit-cancel"
              >
                Renunță
              </button>
              <button
                onClick={saveEdit}
                disabled={busy || !isDirty}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white px-5 py-2 text-xs font-semibold disabled:opacity-50"
                data-testid="enr-edit-save"
              >
                <Save size={13} /> {busy ? "Se salvează…" : "Salvează modificările"}
              </button>
            </div>
          </div>
        )}
      </aside>
      {groupEmailAsk && (
        <GroupEmailModal enrollment={groupEmailAsk} busy={busy}
          onCancel={() => {
            setGroupEmailAsk(null);
            toast.message("Nu am trimis emailul. Îl poți trimite oricând din dreptul grupei.");
          }}
          onConfirm={sendGroupEmail} />
      )}
    </div>
  );
}

// The question after a move, and what the email will carry. A dialog rather
// than a browser prompt: the operator has to see the group and the schedule
// before deciding (28.09.2026).
function GroupEmailModal({ enrollment, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onCancel} role="presentation" data-testid="enr-group-email-confirm">
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        className="w-full max-w-md rounded-3xl border border-[#E5E7EB] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#E5E7EB] p-5">
          <h3 className="font-display text-lg font-bold text-[#373A45] inline-flex items-center gap-2">
            <Send size={18} /> Noul program pe email
          </h3>
          <button type="button" onClick={onCancel} aria-label="Închide" className="text-[#6B7280] hover:text-[#373A45]">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 p-5 text-sm text-[#373A45]">
          <p>{groupChangeQuestion(enrollment)}</p>
          <p className="text-xs text-[#6B7280]">{scheduleNote(enrollment)}</p>
          <p className="rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] p-3 text-xs text-[#6B7280]">
            Emailul pleacă la <strong>{enrollment.email}</strong> și nu poartă linkul de acces:
            acela ajunge prin reamintirile grupei: cu 48 de ore înainte de prima sesiune și cu o oră înainte de fiecare.
          </p>
        </div>
        <div className="flex justify-end gap-2 border-t border-[#E5E7EB] bg-[#F5F6F6]/60 p-5">
          <button type="button" onClick={onCancel}
            className="rounded-full border border-[#E5E7EB] px-4 py-2 text-sm hover:bg-white">
            Nu trimite
          </button>
          <button type="button" onClick={onConfirm} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] px-5 py-2 text-sm font-semibold text-white hover:bg-[#7A4986] disabled:opacity-60">
            <Send size={14} /> Trimite programul
          </button>
        </div>
      </div>
    </div>
  );
}

function EditForm({ form, setForm, groups, data, busy }) {
  // Editable form mirroring the public enrollment shape. Field-level changes
  // are kept locally; the parent drawer diffs against `data` and PATCHes
  // only what's dirty. We deliberately do NOT show analytics / payment IDs
  // / dedup ids here — those are read-only by policy.
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="p-6 space-y-4 text-sm overflow-y-auto flex-1">
      <div className="grid grid-cols-2 gap-3">
        <EditableField label="Prenume" testId="edit-prenume">
          <input value={form.prenume} onChange={upd("prenume")} disabled={busy}
                 autoComplete="given-name" autoCapitalize="words"
                 className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none" />
        </EditableField>
        <EditableField label="Nume" testId="edit-nume">
          <input value={form.nume} onChange={upd("nume")} disabled={busy}
                 autoComplete="family-name" autoCapitalize="words"
                 className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none" />
        </EditableField>
      </div>
      <EditableField label="Email" testId="edit-email">
        <input type="email" value={form.email} onChange={upd("email")} disabled={busy}
               autoComplete="email" inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
               className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none" />
      </EditableField>
      <EditableField label="Telefon" testId="edit-telefon">
        <input type="tel" value={form.telefon} onChange={upd("telefon")} disabled={busy}
               autoComplete="tel" inputMode="tel"
               placeholder="07XXXXXXXX sau +407XXXXXXXX"
               className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none" />
      </EditableField>
      <div className="grid grid-cols-2 gap-3">
        <EditableField label="Cod școală (citit)" testId="edit-school-code">
          <input
            value={form.school_code || ""}
            disabled
            placeholder={form.manual_school_entry ? "manual" : "—"}
            className="w-full rounded-lg border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2 text-xs font-mono text-[#6B7280]"
          />
        </EditableField>
      </div>
      <div className="mt-1">
        <TeacherProfileFields
          value={{
            teaching_levels: form.teaching_levels || [],
            teaching_level_other: form.teaching_level_other || "",
            specialization: form.specialization || "",
            specialization_other: form.specialization_other || "",
          }}
          onChange={(patch) => setForm((f) => {
            const next = { ...f, ...patch };
            if ("specialization" in patch || "specialization_other" in patch) {
              const spec = patch.specialization ?? f.specialization;
              const other = patch.specialization_other ?? f.specialization_other;
              next.specializare = spec === "Altă disciplină" ? (other || "") : (spec || "");
            }
            return next;
          })}
          required={false}
        />
      </div>
      <div className="mt-1">
        <SchoolSelector
          value={{
            county: form.county,
            uat_name: form.uat_name,
            school_name: form.unitate,
            school_code: form.school_code,
            city_original: form.city_original,
            manual_school_entry: form.manual_school_entry,
          }}
          onChange={(patch) => {
            setForm((f) => {
              const next = { ...f };
              if ("county" in patch) next.county = patch.county;
              if ("uat_name" in patch) { next.uat_name = patch.uat_name; next.city = patch.uat_name || ""; }
              if ("school_name" in patch) next.unitate = patch.school_name;
              if ("school_code" in patch) next.school_code = patch.school_code;
              if ("city_original" in patch) next.city_original = patch.city_original;
              if ("manual_school_entry" in patch) next.manual_school_entry = patch.manual_school_entry;
              return next;
            });
          }}
          required={false}
          compact
        />
      </div>
      <EditableField label="Grupă" testId="edit-group">
        <select value={form.group_id || ""} onChange={upd("group_id")} disabled={busy}
                className="w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none">
          <option value="">— fără grupă (necesită atribuire) —</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}{g.start_date ? ` · ${new Date(g.start_date).toLocaleDateString("ro-RO")}` : ""}
            </option>
          ))}
          {/* Preserve the current group if it's no longer active so the option
              stays selectable until the admin picks something else. */}
          {form.group_id && !groups.find((g) => g.id === form.group_id) && (
            <option value={form.group_id}>{data.group_snapshot?.name || form.group_id} (arhivat)</option>
          )}
        </select>
      </EditableField>
      <div className="grid grid-cols-2 gap-3">
        <EditableField label="Status înscriere" testId="edit-status">
          <select value={form.status} onChange={upd("status")} disabled={busy}
                  className="w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none">
            {STATUSES.map((s) => (
              <option key={s.v} value={s.v}>{s.label}</option>
            ))}
          </select>
        </EditableField>
        <EditableField label="Etapă CRM" testId="edit-crm-stage">
          <select value={form.crm_stage} onChange={upd("crm_stage")} disabled={busy}
                  className="w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none">
            {CRM_STAGES.map((s) => (
              <option key={s.v} value={s.v}>{s.label}</option>
            ))}
          </select>
        </EditableField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <EditableField label="Preferință zile curs" testId="edit-preferred-days">
          <select value={form.preferred_course_days || ""} onChange={upd("preferred_course_days")} disabled={busy}
                  className="w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none">
            <option value="">— neselectat —</option>
            <option value="Doar în cursul săptămânii">Doar în cursul săptămânii</option>
            <option value="Doar sâmbăta">Doar sâmbăta</option>
            <option value="În cursul săptămânii și sâmbăta">În cursul săptămânii și sâmbăta</option>
            <option value="Altă variantă">Altă variantă</option>
          </select>
          {form.preferred_course_days === "Altă variantă" && (
            <input
              type="text"
              maxLength={200}
              value={form.preferred_course_days_other || ""}
              onChange={upd("preferred_course_days_other")}
              disabled={busy}
              placeholder="Descrie preferința (max. 200 caractere)"
              className="mt-2 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none"
              data-testid="edit-preferred-days-other"
              autoComplete="off"
            />
          )}
        </EditableField>
        <EditableField label="Preferință parte a zilei" testId="edit-preferred-time">
          <select value={form.preferred_time_of_day || ""} onChange={upd("preferred_time_of_day")} disabled={busy}
                  className="w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none">
            <option value="">— neselectat —</option>
            <option value="Dimineață">Dimineață</option>
            <option value="Prânz">Prânz</option>
            <option value="Seară">Seară</option>
            <option value="Altă variantă">Altă variantă</option>
          </select>
          {form.preferred_time_of_day === "Altă variantă" && (
            <input
              type="text"
              maxLength={200}
              value={form.preferred_time_of_day_other || ""}
              onChange={upd("preferred_time_of_day_other")}
              disabled={busy}
              placeholder="Descrie intervalul (max. 200 caractere)"
              className="mt-2 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:border-[#965EA7] focus:ring-1 focus:ring-[#965EA7]/30 outline-none"
              data-testid="edit-preferred-time-other"
              autoComplete="off"
            />
          )}
        </EditableField>
      </div>
      <div className="rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] p-3 text-xs text-[#6B7280] leading-relaxed">
        Informațiile generate automat (ID plată, factură, tracking, event_id) nu pot fi modificate manual.
        Modificările vor fi înregistrate în jurnalul de audit.
      </div>
    </div>
  );
}

function EditableField({ label, testId, children }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block text-[11px] font-semibold uppercase tracking-widest text-[#6B7280] mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

/**
 * Unified Activity + Notes panel for an enrollment.
 *
 * Renders inside the drawer. Fetches `/admin/enrollments/{id}/timeline` which
 * already merges notes, lifecycle events, CRM stage transitions and audit
 * logs in reverse-chronological order. The textarea (owner+admin) appends a
 * note and prepends it optimistically before refetching the server stream.
 *
 * Refetches when `refreshKey` (the parent passes the doc's `updated_at`)
 * changes — that fires after any PATCH/group-assign so the stream stays in
 * sync without a full drawer reload.
 */
function ActivityTimeline({ enrollmentId, canMutate, refreshKey }) {
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/enrollments/${enrollmentId}/timeline`);
      setItems(r.data?.items || []);
    } catch (e) {
      // 401/403 already toasted globally; render empty stream so the UI is graceful.
      setItems([]);
    }
  }, [enrollmentId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    // Optimistic prepend so the operator sees the note immediately.
    const optimistic = {
      kind: "note", ts: new Date().toISOString(),
      actor: "tu", text, id: `optimistic-${Date.now()}`,
    };
    setItems((cur) => [optimistic, ...(cur || [])]);
    try {
      await api.post(`/admin/enrollments/${enrollmentId}/notes`, { text });
      setDraft("");
      toast.success("Notiță salvată");
      await load();
    } catch (err) {
      // Roll back the optimistic entry.
      setItems((cur) => (cur || []).filter((it) => it.id !== optimistic.id));
      toast.error(err?.response?.data?.detail || "Eroare la salvarea notiței");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4 space-y-3" data-testid="enr-activity-panel">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-sm font-bold text-[#373A45]">Activitate &amp; Notițe</h4>
        <span className="text-[10px] uppercase tracking-widest text-[#6B7280]">
          {items === null ? "" : `${items.length} ${items.length === 1 ? "intrare" : "intrări"}`}
        </span>
      </div>

      {canMutate && (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Adaugă o notiță despre această înscriere..."
            rows={3}
            disabled={busy}
            className="w-full resize-none rounded-lg border border-[#E5E7EB] bg-[#F8F8F9] px-3 py-2 text-sm text-[#373A45] placeholder:text-[#9CA3AF] focus:border-[#965EA7] focus:bg-white focus:ring-1 focus:ring-[#965EA7]/30 outline-none"
            data-testid="enr-note-input"
          />
          <div className="flex justify-end">
            <button
              onClick={submit}
              disabled={busy || !draft.trim()}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white px-4 py-1.5 text-xs font-semibold disabled:opacity-50"
              data-testid="enr-note-save"
            >
              <Send size={12} /> {busy ? "Se salvează…" : "Salvează notița"}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3 pt-1" data-testid="enr-timeline">
        {items === null && <div className="text-xs text-[#6B7280]">Se încarcă activitatea…</div>}
        {items !== null && items.length === 0 && (
          <div className="text-xs text-[#6B7280] italic">Nicio activitate înregistrată încă.</div>
        )}
        {(items || []).map((it, idx) => (
          <TimelineItem key={it.id || `${it.kind}-${it.ts}-${idx}`} item={it} />
        ))}
      </div>
    </div>
  );
}

// Map a timeline item to a small icon + a human label. Keeps render concise.
const TIMELINE_LABELS = {
  enrollment_updated: { icon: Edit3,        label: "Date actualizate",     tone: "text-[#3F8EBA]" },
  enrollment_group_assigned: { icon: UserCheck, label: "Grupă atribuită",  tone: "text-[#519872]" },
  enrollment_soft_deleted: { icon: AlertCircle, label: "Înscriere ștearsă", tone: "text-[#9b1f2c]" },
  enrollment_restored: { icon: RotateCcw,   label: "Înscriere restaurată", tone: "text-[#519872]" },
  group_assigned:    { icon: UserCheck,     label: "Grupă atribuită",      tone: "text-[#519872]" },
  note_added:        { icon: MessageSquare, label: "Notiță",               tone: "text-[#965EA7]" },
  // The participant filled the public form again. Their entries are NOT
  // written over the record — the details carry what differed, for an
  // operator to apply or ignore.
  resubmitted_at_checkout: { icon: RotateCcw, label: "Formular retrimis",   tone: "text-[#7A4986]" },
  // An older attempt came back refused after the enrollment was paid: kept
  // for the record, without touching the status (services/payments/paid_guard).
  payment_attempt_superseded: { icon: History, label: "Încercare de plată mai veche", tone: "text-[#6B7280]" },
  payment_status_restored: { icon: RotateCcw, label: "Status corectat: plătită", tone: "text-[#519872]" },
  // Shown with their internal names until 19.10.2026.
  enrollment_created: { icon: History, label: "Înscriere creată", tone: "text-[#519872]" },
  enrollment_assigned: { icon: UserCheck, label: "Responsabil atribuit", tone: "text-[#3F8EBA]" },
  group_changed: { icon: UserCheck, label: "Grupă schimbată", tone: "text-[#519872]" },
  group_change_notified: { icon: MessageSquare, label: "Programul nou trimis pe email", tone: "text-[#3F8EBA]" },
  payment_amount_customized: { icon: Edit3, label: "Sumă de plată modificată", tone: "text-[#3F8EBA]" },
  payment_link_created: { icon: History, label: "Link de plată trimis", tone: "text-[#3F8EBA]" },
  payment_link_resent: { icon: RotateCcw, label: "Link de plată retrimis", tone: "text-[#3F8EBA]" },
  payment_link_clicked: { icon: History, label: "Linkul de plată a fost deschis", tone: "text-[#6B7280]" },
  payment_reminder_sent: { icon: MessageSquare, label: "Reamintire de plată trimisă", tone: "text-[#3F8EBA]" },
  checkout_started: { icon: History, label: "Plată începută", tone: "text-[#6B7280]" },
  enrollment_payment_expired: { icon: AlertCircle, label: "Termenul de plată a expirat", tone: "text-[#9b1f2c]" },
  email_resent: { icon: RotateCcw, label: "Email retrimis", tone: "text-[#3F8EBA]" },
};

const ATTEMPT_STATUS = { failed: "eșuată", cancelled: "anulată", pending: "în așteptare", abandoned: "abandonată" };

function TimelineItem({ item }) {
  const isNote = item.kind === "note";
  const isCrm = item.kind === "crm_stage_change";
  const meta = TIMELINE_LABELS[item.type] || (
    isCrm ? { icon: GitBranch, label: "Etapă CRM modificată", tone: "text-[#965EA7]" }
          : { icon: History, label: item.type || "Eveniment", tone: "text-[#6B7280]" }
  );
  const Icon = isNote ? MessageSquare : meta.icon;
  const tone = isNote ? "text-[#965EA7]" : meta.tone;

  return (
    <div className="flex items-start gap-3" data-testid={`timeline-item-${item.kind}`}>
      <div className={`mt-0.5 h-7 w-7 rounded-full bg-[#F5F6F6] grid place-items-center shrink-0 ${tone}`}>
        <Icon size={13} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[13px] font-semibold text-[#373A45]">
            {isNote ? "Notiță adăugată" : meta.label}
          </span>
          <span className="text-[11px] text-[#6B7280]">{relativeTime(item.ts)}</span>
        </div>
        <TimelineItemBody item={item} />
        {item.actor && (
          <div className="text-[11px] text-[#9CA3AF] mt-0.5">de {item.actor}</div>
        )}
      </div>
    </div>
  );
}

function TimelineItemBody({ item }) {
  if (item.kind === "note") {
    return (
      <p className="mt-1 text-[13px] text-[#373A45] leading-relaxed whitespace-pre-wrap break-words">
        {item.text}
      </p>
    );
  }
  if (item.kind === "crm_stage_change") {
    const fromL = CRM_BY[item.details?.from]?.label || item.details?.from || "—";
    const toL = CRM_BY[item.details?.to]?.label || item.details?.to || "—";
    return <div className="mt-0.5 text-[12px] text-[#6B7280]">{fromL} → <span className="text-[#373A45]">{toL}</span></div>;
  }
  if (item.type === "enrollment_updated") {
    const changes = item.details?.changes || {};
    const entries = Object.entries(changes);
    if (entries.length === 0) {
      return <div className="mt-0.5 text-[12px] text-[#6B7280]">Modificare fără diff disponibil.</div>;
    }
    return (
      <ul className="mt-0.5 space-y-0.5 text-[12px]">
        {entries.slice(0, 6).map(([field, vals]) => (
          <li key={field} className="text-[#6B7280]">
            <span className="font-semibold text-[#373A45]">{FIELD_LABELS[field] || field}</span>:{" "}
            <span className="line-through opacity-60">{formatVal(vals?.old)}</span>{" "}→{" "}
            <span className="text-[#373A45]">{formatVal(vals?.new)}</span>
          </li>
        ))}
        {entries.length > 6 && (
          <li className="text-[11px] text-[#9CA3AF]">+{entries.length - 6} alte modificări</li>
        )}
      </ul>
    );
  }
  if (item.type === "enrollment_group_assigned" || item.type === "group_assigned") {
    const name = item.details?.group_name || item.details?.to_group_id || "—";
    return <div className="mt-0.5 text-[12px] text-[#6B7280]">→ <span className="text-[#373A45]">{name}</span></div>;
  }
  if (item.type === "enrollment_soft_deleted" && item.details?.reason) {
    return <div className="mt-0.5 text-[12px] text-[#6B7280]">Motiv: {item.details.reason}</div>;
  }
  if (item.type === "payment_attempt_superseded") {
    const d = item.details || {};
    const msg = (d.message || "").trim();
    return (
      <div className="mt-0.5 text-[12px] text-[#6B7280]" data-testid="timeline-payment-superseded">
        {ATTEMPT_STATUS[d.attempt_status] || d.attempt_status || "fără succes"}
        {msg ? ` (${glossFor(msg) || msg})` : ""}. Înscrierea rămâne plătită{d.paid_invoice ? ` (${d.paid_invoice})` : ""}.
      </div>
    );
  }
  if (item.type === "payment_status_restored") {
    return <div className="mt-0.5 text-[12px] text-[#6B7280]">{item.details?.message}</div>;
  }
  if (item.type === "payment_failed" || item.type === "payment_cancelled") {
    // A bare "payment_failed · de system" tells whoever is reading it nothing
    // they did not already know from the status. The reason is the point.
    const msg = (item.details?.message || "").trim();
    if (!msg) return null;
    return (
      <div className="mt-0.5 text-[12px] text-[#9b1f2c]" data-testid="timeline-payment-reason">
        {glossFor(msg) || msg}
      </div>
    );
  }
  return null;
}

const FIELD_LABELS = {
  nume: "Nume", prenume: "Prenume", email: "Email", telefon: "Telefon",
  specializare: "Specializare", unitate: "Unitate", city: "Localitate",
  county: "Județ", status: "Status", crm_stage: "Etapă CRM",
  group_id: "Grupă (ID)", group_snapshot: "Snapshot grupă",
  needs_group_assignment: "Necesită atribuire",
};

// Lightweight Romanian relative-time formatter (no extra dependency).
function relativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "acum câteva secunde";
  const min = Math.floor(sec / 60);
  if (min < 60) return `acum ${min} min${min === 1 ? "" : ""}`;
  const h = Math.floor(min / 60);
  if (h < 24) return `acum ${h} ${h === 1 ? "oră" : "ore"}`;
  const d = Math.floor(h / 24);
  if (d < 7) return `acum ${d} ${d === 1 ? "zi" : "zile"}`;
  return new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" });
}

function formatVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Da" : "Nu";
  if (typeof v === "object") return JSON.stringify(v).slice(0, 40);
  const s = String(v);
  return s.length > 40 ? `${s.slice(0, 37)}…` : s;
}

function Field({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-baseline">
      <div className="text-xs uppercase tracking-widest text-[#6B7280]">{label}</div>
      <div className="col-span-2 text-[#373A45]">{value}</div>
    </div>
  );
}

/**
 * Why the card was refused.
 *
 * The gateway sends the reason with every notification and we have always
 * stored it, but it was only ever rendered inside a diagnostic for
 * notifications whose signature failed to verify. Fixing that bug removed the
 * only place the reason appeared — the display had been attached to the fault
 * rather than to the fact.
 *
 * Renders nothing for a payment that simply never happened: an abandoned
 * checkout has no refusal behind it, and inventing one is the exact confusion
 * this whole investigation started from.
 */
function PaymentFailureBox({ payment }) {
  const reason = failureReason(payment);
  if (!reason) return null;
  return (
    <div
      className="mt-3 rounded-xl border border-[#E63946]/30 bg-[#E63946]/5 px-3 py-2.5"
      data-testid="enr-payment-failure-reason"
    >
      <div className="text-[10px] uppercase tracking-widest font-semibold text-[#9b1f2c]">
        {reason.cancelled ? "Plată anulată" : "Motivul refuzului"}
      </div>
      <div className="mt-1 text-sm font-semibold text-[#9b1f2c]">
        {reason.gloss || reason.message}
      </div>
      {reason.gloss && (
        /* The bank's own wording stays visible under the translation — it is
           what EuPlătesc support will ask for, and it is the authority. */
        <div className="mt-0.5 text-[11px] text-[#9b1f2c]/70 font-mono">{reason.message}</div>
      )}
      {reason.approval && (
        <div className="mt-1 text-[11px] text-[#92400E]">
          Atenție: refuzul are cod de aprobare ({reason.approval}) — contradictoriu, merită verificat.
        </div>
      )}
    </div>
  );
}


function DeleteModal({ state, onChange, onCancel, onConfirm }) {
  const isBulk = state.ids.length > 1;
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={onCancel} data-testid="enrollments-delete-confirm">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl border border-[#E5E7EB] shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
          <h3 className="font-display text-lg font-bold text-[#9b1f2c] inline-flex items-center gap-2">
            <Trash2 size={18} /> Șterge {isBulk ? `${state.ids.length} înscrieri` : "înscriere"}
          </h3>
          <button onClick={onCancel} className="text-[#6B7280] hover:text-[#373A45]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3 text-sm text-[#373A45]">
          <p>Soft-delete: rândul rămâne în DB cu <code className="bg-[#EDEEF0] px-1 rounded text-xs">deleted_at</code> setat și poate fi restaurat.</p>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest">Motiv</span>
            <input type="text" value={state.reason} onChange={(e) => onChange({ ...state, reason: e.target.value })} placeholder="ex. test cleanup"
                   className="mt-1 w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm" />
          </label>
          {state.retry409 && (
            <div className="rounded-xl bg-[#FDF3E7] border border-[#F59E0B]/40 p-3 text-xs text-[#8a6a1a]">
              Cel puțin o înscriere are plată LIVE confirmată. Bifează ca să continui.
            </div>
          )}
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={state.confirm_paid_live} onChange={(e) => onChange({ ...state, confirm_paid_live: e.target.checked })} className="accent-[#9b1f2c]" />
            Confirm că pot șterge înscrieri cu plată live confirmată
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[#E5E7EB] bg-[#F5F6F6]/60">
          <button onClick={onCancel} className="rounded-full px-4 py-2 text-sm border border-[#E5E7EB] hover:bg-white">Anulează</button>
          <button onClick={onConfirm} className="inline-flex items-center gap-1.5 rounded-full bg-[#9b1f2c] hover:bg-[#7e1923] text-white px-5 py-2 text-sm font-semibold">
            <Trash2 size={14} /> Șterge {isBulk ? state.ids.length : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

const ENR_ADV_LS_KEY = "fped_enrollments_adv_open";

function EnrollmentsFilterBar({ filters, setFilters, qDraft, setQDraft, isOwner, currentRole, onReload, onExport, onExportMinistry, onClear, filtersActive, expiredTotal = 0 }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(ENR_ADV_LS_KEY) === "1"; } catch { return false; }
  });
  const toggle = () => setOpen((v) => {
    const next = !v;
    try { localStorage.setItem(ENR_ADV_LS_KEY, next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });

  // Active advanced filter counter — surfaces a "•" so users notice the disclosure.
  const advCount = [
    filters.specializare, filters.email, filters.enrollment_mode,
    filters.crm_stage, filters.consent_marketing,
    filters.date_from, filters.date_to,
    filters.test_only ? "1" : "", filters.include_deleted ? "1" : "", filters.expired_only ? "1" : "",
  ].filter(Boolean).length;

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl" data-testid="enrollments-filter-bar">
      {/* Quick filters */}
      <div className="flex flex-wrap items-center gap-2 p-2.5">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            placeholder="Caută în nume, email, telefon, unitate, oraș, județ…"
            className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-full pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#965EA7]"
            data-testid="enrollments-search"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
          data-testid="enrollments-filter-status"
        >
          <option value="">Toate statusurile</option>
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <select
          value={filters.ministry_status}
          onChange={(e) => setFilters({ ...filters, ministry_status: e.target.value })}
          className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
          data-testid="enrollments-filter-ministry"
          title="Stadiul datelor pentru dosarul de acreditare"
        >
          <option value="">Date minister: toate</option>
          {Object.entries(MINISTRY_BY).map(([v, m]) => (
            <option key={v} value={v}>{m.label}</option>
          ))}
        </select>
        <select
          value={filters.regime}
          onChange={(e) => setFilters({ ...filters, regime: e.target.value })}
          className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
          data-testid="enrollments-filter-regime"
          title="Cursul acreditat de minister sau Curs FPED"
        >
          <option value="">Regim: toate</option>
          <option value="acreditat">Acreditat MEC</option>
          <option value="fped">Curs FPED</option>
        </select>
        <select
          value={filters.referral}
          onChange={(e) => setFilters({ ...filters, referral: e.target.value })}
          className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
          data-testid="enrollments-filter-referral"
          title="Înscrieri venite printr-un cod de recomandare, sau cursanți care au recomandat pe cineva"
        >
          <option value="">Recomandări: toate</option>
          <option value="referred">Veniți prin recomandare</option>
          <option value="referrer">Au recomandat pe cineva</option>
        </select>
        <div className="w-40">
          <CountyCombobox
            options={ROMANIAN_COUNTIES}
            value={filters.county}
            onChange={(v) => setFilters({ ...filters, county: v })}
            placeholder="Toate județele"
            testId="enrollments-filter-county"
          />
        </div>
        <select
          value={filters.friendly_source}
          onChange={(e) => setFilters({ ...filters, friendly_source: e.target.value })}
          className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
          data-testid="enrollments-filter-channel"
        >
          <option value="">Toate canalele</option>
          {FRIENDLY_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          onClick={toggle}
          className={`text-sm rounded-full px-3 py-2 border inline-flex items-center gap-1.5 transition-colors ${open ? "border-[#965EA7]/40 bg-[#F5F0F8] text-[#7A4986]" : "border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]"}`}
          data-testid="enrollments-advanced-toggle"
          aria-expanded={open}
        >
          <SlidersHorizontal size={14} /> Avansate
          {advCount > 0 && (
            <span className="rounded-full bg-[#965EA7] text-white text-[10px] font-bold px-1.5 leading-none py-0.5">{advCount}</span>
          )}
          <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={onReload} className="text-sm rounded-full px-3 py-2 border border-[#E5E7EB] hover:bg-[#F5F6F6] inline-flex items-center gap-1.5" data-testid="enrollments-reload">
            <RefreshCcw size={14} />
          </button>
          <ExportButton
            onExport={onExport}
            className="text-sm rounded-full px-3 py-2 bg-[#373A45] text-white hover:bg-[#1F2128] inline-flex items-center gap-1.5"
            testid="enrollments-export-csv"
            title="Exportă înscrierile filtrate curent"
          />
          {/* Owner and admin FPED (decided 19.10.2026): a colleague prepares the dossier too. */}
          {(isOwner || ["admin_fped", "admin"].includes(currentRole)) && (
            <ExportButton
              onExport={onExportMinistry}
              label="Export dosar"
              className="text-sm rounded-full px-3 py-2 border-2 border-[#965EA7] text-[#965EA7] hover:bg-[#965EA7] hover:text-white inline-flex items-center gap-1.5 transition-colors"
              testid="enrollments-export-ministry"
              title="Exportă datele pentru dosarul de acreditare — conține CNP-uri, accesul este înregistrat"
            />
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-[#E5E7EB] p-2.5 flex flex-wrap items-center gap-2 animate-in fade-in slide-in-from-top-1 duration-150" data-testid="enrollments-advanced-panel">
          <select
            value={filters.crm_stage}
            onChange={(e) => setFilters({ ...filters, crm_stage: e.target.value })}
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
            data-testid="enrollments-filter-crm-stage"
          >
            <option value="">Toate etapele CRM</option>
            {CRM_STAGES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
          <select
            value={filters.enrollment_mode}
            onChange={(e) => setFilters({ ...filters, enrollment_mode: e.target.value })}
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
            data-testid="enrollments-filter-mode"
          >
            <option value="">Toate modurile</option>
            <option value="online_payment">Plată online</option>
            <option value="registration_only">Fără plată</option>
          </select>
          <input
            value={filters.specializare}
            onChange={(e) => setFilters({ ...filters, specializare: e.target.value })}
            placeholder="Specializare"
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white w-44"
            data-testid="enrollments-filter-specializare"
            autoComplete="off"
          />
          <input
            value={filters.email}
            onChange={(e) => setFilters({ ...filters, email: e.target.value })}
            placeholder="Email exact"
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white w-52"
            data-testid="enrollments-filter-email"
            autoComplete="off"
          />
          <select
            value={filters.consent_marketing}
            onChange={(e) => setFilters({ ...filters, consent_marketing: e.target.value })}
            className="rounded-full px-3 py-2 text-sm border border-[#E5E7EB] bg-white"
            data-testid="enrollments-filter-consent"
          >
            <option value="">Consent: oricare</option>
            <option value="yes">Cu consent marketing</option>
            <option value="no">Fără consent</option>
          </select>
          <label className="text-xs inline-flex items-center gap-1.5 text-[#373A45]">
            De la
            <input
              type="date"
              value={filters.date_from?.slice(0, 10) || ""}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value ? `${e.target.value}T00:00:00Z` : "" })}
              className="rounded-full px-3 py-1 text-xs border border-[#E5E7EB] bg-white"
              data-testid="enrollments-filter-date-from"
            />
          </label>
          <label className="text-xs inline-flex items-center gap-1.5 text-[#373A45]">
            Până la
            <input
              type="date"
              value={filters.date_to?.slice(0, 10) || ""}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value ? `${e.target.value}T23:59:59Z` : "" })}
              className="rounded-full px-3 py-1 text-xs border border-[#E5E7EB] bg-white"
              data-testid="enrollments-filter-date-to"
            />
          </label>
          {isOwner && (
            <>
              <label className="text-xs inline-flex items-center gap-1.5 text-[#373A45] cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.test_only}
                  onChange={(e) => setFilters({ ...filters, test_only: e.target.checked })}
                  className="accent-[#9b1f2c]"
                  data-testid="enrollments-filter-testonly"
                />
                Doar test
              </label>
              <label className="text-xs inline-flex items-center gap-1.5 text-[#373A45] cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.include_deleted}
                  onChange={(e) => setFilters({ ...filters, include_deleted: e.target.checked })}
                  className="accent-[#965EA7]"
                  data-testid="enrollments-filter-deleted"
                />
                Arată șterse
              </label>
            </>
          )}
          {(expiredTotal > 0 || filters.expired_only) && (
            <button
              type="button"
              onClick={() => setFilters({ ...filters, expired_only: !filters.expired_only })}
              className={`text-xs rounded-full px-3 py-1 font-semibold border ${filters.expired_only
                ? "border-[#8a6a1a] bg-[#FDF3E7] text-[#8a6a1a]"
                : "border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F5F6F6]"}`}
              data-testid="enrollments-filter-expired"
              title="Înscrieri închise pentru că nu au fost plătite la termen"
            >
              {filters.expired_only ? "← Înapoi la înscrieri" : `Expirate (${expiredTotal})`}
            </button>
          )}
          {(currentRole === "owner" || currentRole === "admin_fped" || currentRole === "admin") && (
            <label className="text-xs inline-flex items-center gap-1.5 text-[#373A45] cursor-pointer">
              <input
                type="checkbox"
                checked={filters.mine_only}
                onChange={(e) => setFilters({ ...filters, mine_only: e.target.checked })}
                className="accent-[#519872]"
                data-testid="enrollments-filter-mineonly"
              />
              Doar ale mele
            </label>
          )}
          {filtersActive && (
            <button onClick={onClear} className="ml-auto text-xs rounded-full px-3 py-1 font-semibold bg-[#FDECEC] text-[#9b1f2c] inline-flex items-center gap-1" data-testid="enrollments-clear-filters">
              <X size={12} /> Resetează
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact colored badge for the CRM stage. Click-to-edit reveals a popover
 * with the six stage options instead of dominating the row with a wide
 * native <select>. Server PATCH happens via the parent `onChange`.
 *
 * The popover is rendered through a Portal into `document.body` and positioned
 * with `position: fixed` so it never gets clipped by scrollable table
 * containers / `overflow:hidden` ancestors. Auto-flips upwards when the bottom
 * of the viewport doesn't leave enough room.
 */
function CrmStageBadge({ value, disabled, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, flip: false });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const POPOVER_WIDTH = 200;          // matches min-w via inline width below
  const ESTIMATED_HEIGHT = 6 * 32 + 8; // 6 options × ~32px + paddings

  // Compute fixed-position coordinates anchored to the trigger button.
  const computePos = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const flip = spaceBelow < ESTIMATED_HEIGHT + 12 && rect.top > ESTIMATED_HEIGHT + 12;
    // Keep popover within viewport horizontally with 8px padding.
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - POPOVER_WIDTH - 8));
    const top = flip ? rect.top - ESTIMATED_HEIGHT - 6 : rect.bottom + 4;
    setPos({ top, left, flip });
  }, [ESTIMATED_HEIGHT]);

  // Re-position synchronously after opening (avoids first-frame flicker).
  useLayoutEffect(() => {
    if (open) computePos();
  }, [open, computePos]);

  // Re-position on scroll/resize while open; close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => computePos();
    const onResize = () => computePos();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDocPointer = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);   // capture: catches inner scrollers
    window.addEventListener("resize", onResize);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDocPointer);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDocPointer);
    };
  }, [open, computePos]);

  return (
    <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold inline-flex items-center gap-1 ${value.cls} ${disabled ? "opacity-60 cursor-default" : "cursor-pointer hover:opacity-90"}`}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {value.label}
        {!disabled && <ChevronDown size={10} className="opacity-70" />}
      </button>
      {open && !disabled && typeof document !== "undefined" && createPortal(
        <div
          ref={popoverRef}
          role="listbox"
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: POPOVER_WIDTH,
            zIndex: 9999,
          }}
          className="bg-white border border-[#E5E7EB] rounded-xl shadow-lg p-1"
          data-testid={`${testId}-popover`}
          onClick={(e) => e.stopPropagation()}
        >
          {CRM_STAGES.map((s) => (
            <button
              key={s.v}
              type="button"
              role="option"
              aria-selected={s.v === value.v}
              onClick={() => { onChange?.(s.v); setOpen(false); }}
              className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs hover:bg-[#F5F6F6] ${s.v === value.v ? "font-semibold text-[#373A45]" : "text-[#373A45]"}`}
              data-testid={`${testId}-option-${s.v}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${s.cls.split(" ")[0]}`} aria-hidden="true" />
              <span className="flex-1">{s.label}</span>
              {s.v === value.v && <Check size={12} className="text-[#965EA7]" />}
            </button>
          ))}
        </div>,
        document.body
      )}
    </span>
  );
}

